import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

/* ─── secret field with show/hide toggle ─────────────────── */
function SecretField({ label, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <TextField
      fullWidth
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Leave blank to keep current value'}
      size="small"
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((p) => !p)} edge="end">
              {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

/* ─── status chip helper ──────────────────────────────────── */
function StatusChip({ enabled }) {
  return (
    <Chip
      size="small"
      icon={enabled ? <CheckCircleIcon /> : <ErrorIcon />}
      label={enabled ? 'Enabled' : 'Disabled'}
      color={enabled ? 'success' : 'default'}
      variant="outlined"
      sx={{ fontWeight: 700 }}
    />
  );
}

/* ─── main component ──────────────────────────────────────── */
export default function AdminPaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  /* form state */
  const [stripe, setStripe] = useState({
    enabled: false,
    testMode: true,
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  });
  const [paypal, setPaypal] = useState({
    enabled: false,
    mode: 'sandbox',
    clientId: '',
    clientSecret: '',
  });
  const [clictopay, setClictopay] = useState({
    enabled: false,
    testMode: true,
    userName: '',
    password: '',
    baseUrlOverride: '',
  });

  /* fetch on mount */
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get('/api/payment-settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data.success && data.settings) {
          const s = data.settings;
          setStripe({
            enabled: s.stripe?.enabled ?? false,
            testMode: s.stripe?.testMode ?? true,
            publishableKey: s.stripe?.publishableKey ?? '',
            secretKey: '',           // never pre-fill secrets
            webhookSecret: '',
          });
          setPaypal({
            enabled: s.paypal?.enabled ?? false,
            mode: s.paypal?.mode ?? 'sandbox',
            clientId: s.paypal?.clientId ?? '',
            clientSecret: '',
          });
          setClictopay({
            enabled: s.clictopay?.enabled ?? false,
            testMode: s.clictopay?.testMode ?? true,
            userName: s.clictopay?.userName ?? '',
            password: '',
            baseUrlOverride: s.clictopay?.baseUrlOverride ?? '',
          });
        }
      })
      .catch((err) => console.error('Failed to load payment settings:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        stripe: { ...stripe },
        paypal: { ...paypal },
        clictopay: { ...clictopay },
      };
      await axios.put('/api/payment-settings', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaveSuccess(true);
      // Clear secret fields on successful save
      setStripe((p) => ({ ...p, secretKey: '', webhookSecret: '' }));
      setPaypal((p) => ({ ...p, clientSecret: '' }));
      setClictopay((p) => ({ ...p, password: '' }));
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      setSaveError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Payment Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure gateway credentials. Secret keys are stored securely and never exposed to the browser.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: '25px', px: 4, fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save All Settings'}
        </Button>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
          Payment settings saved successfully.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* ── Stripe ──────────────────────────────────────── */}
        <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <CreditCardIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                Stripe
              </Typography>
              <Box ml="auto" mr={1}>
                <StatusChip enabled={stripe.enabled} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2.5}>
              <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={stripe.enabled}
                      onChange={(e) => setStripe((p) => ({ ...p, enabled: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Enable Stripe"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={stripe.testMode}
                      onChange={(e) => setStripe((p) => ({ ...p, testMode: e.target.checked }))}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      Test Mode{' '}
                      <Chip
                        size="small"
                        label={stripe.testMode ? 'Test' : 'Live'}
                        color={stripe.testMode ? 'warning' : 'error'}
                        sx={{ ml: 0.5, fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              </Box>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Publishable Key"
                    value={stripe.publishableKey}
                    onChange={(e) => setStripe((p) => ({ ...p, publishableKey: e.target.value }))}
                    placeholder={stripe.testMode ? 'pk_test_…' : 'pk_live_…'}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SecretField
                    label="Secret Key"
                    value={stripe.secretKey}
                    onChange={(e) => setStripe((p) => ({ ...p, secretKey: e.target.value }))}
                    placeholder={stripe.testMode ? 'sk_test_… (leave blank to keep current)' : 'sk_live_…'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SecretField
                    label="Webhook Secret"
                    value={stripe.webhookSecret}
                    onChange={(e) => setStripe((p) => ({ ...p, webhookSecret: e.target.value }))}
                    placeholder="whsec_… (leave blank to keep current)"
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Get your keys from the{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">
                  Stripe Dashboard → Developers → API keys
                </a>
                . For webhook secret, go to{' '}
                <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer">
                  Webhooks
                </a>{' '}
                and add endpoint <code>/api/payments/stripe/webhook</code>.
              </Alert>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ── PayPal ──────────────────────────────────────── */}
        <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <PaymentIcon sx={{ color: '#0070ba' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                PayPal
              </Typography>
              <Box ml="auto" mr={1}>
                <StatusChip enabled={paypal.enabled} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2.5}>
              <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={paypal.enabled}
                      onChange={(e) => setPaypal((p) => ({ ...p, enabled: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Enable PayPal"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={paypal.mode === 'live'}
                      onChange={(e) => setPaypal((p) => ({ ...p, mode: e.target.checked ? 'live' : 'sandbox' }))}
                      color="error"
                    />
                  }
                  label={
                    <Box>
                      Mode{' '}
                      <Chip
                        size="small"
                        label={paypal.mode === 'live' ? 'Live' : 'Sandbox'}
                        color={paypal.mode === 'live' ? 'error' : 'warning'}
                        sx={{ ml: 0.5, fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              </Box>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Client ID"
                    value={paypal.clientId}
                    onChange={(e) => setPaypal((p) => ({ ...p, clientId: e.target.value }))}
                    placeholder="Client ID from PayPal Developer Dashboard"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SecretField
                    label="Client Secret"
                    value={paypal.clientSecret}
                    onChange={(e) => setPaypal((p) => ({ ...p, clientSecret: e.target.value }))}
                    placeholder="Leave blank to keep current value"
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Get credentials from{' '}
                <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noreferrer">
                  PayPal Developer Dashboard → My Apps &amp; Credentials
                </a>
                . Use Sandbox for testing, Live for production.
              </Alert>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ── ClicToPay ───────────────────────────── */}
        <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <CreditCardIcon sx={{ color: '#0f8a8a' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                ClicToPay (TND)
              </Typography>
              <Box ml="auto" mr={1}>
                <StatusChip enabled={clictopay.enabled} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={clictopay.enabled}
                      onChange={(e) => setClictopay((p) => ({ ...p, enabled: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Enable ClicToPay"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={clictopay.testMode}
                      onChange={(e) => setClictopay((p) => ({ ...p, testMode: e.target.checked }))}
                      color="warning"
                    />
                  }
                  label="Test mode"
                />
              </Stack>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Merchant username"
                    value={clictopay.userName}
                    onChange={(e) => setClictopay((p) => ({ ...p, userName: e.target.value }))}
                    placeholder="Provided by your bank / SMT"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SecretField
                    label="Merchant password"
                    value={clictopay.password}
                    onChange={(e) => setClictopay((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Leave blank to keep current value"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Gateway base URL (optional override)"
                    value={clictopay.baseUrlOverride}
                    onChange={(e) => setClictopay((p) => ({ ...p, baseUrlOverride: e.target.value }))}
                    placeholder="Only if your acquirer gave you a different host"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Register these URLs with ClicToPay:
                <Box component="code" sx={{ display: 'block', mt: 1, fontSize: 13 }}>
                  https://vitria.tn/api/payments/clictopay/return
                  <br />
                  https://vitria.tn/api/payments/clictopay/fail
                  <br />
                  https://vitria.tn/api/payments/clictopay/webhook
                </Box>
              </Alert>
            </Stack>
          </AccordionDetails>
        </Accordion>

      </Stack>
    </Box>
  );
}
